/**
 * Souls Wishlist System
 * Isolated architecture for Shopify product wishlists.
 */

class SoulsWishlist {
  constructor() {
    this.storageKey = 'souls_wishlist';
    this.wishlist = this.getWishlist();
    this.container = document.getElementById('souls-wishlist-container');
    this.pageGrid = document.getElementById('souls-wishlist-grid');
    this.init();
  }

  init() {
    this.updateBadges();
    this.renderItems();
    this.syncButtons();
    
    // Listen for custom wishlist updates
    document.addEventListener('souls:wishlist:update', (e) => {
      this.wishlist = this.getWishlist();
      this.updateBadges();
      this.renderItems();
      this.syncButtons();
    });

    // Delegate wishlist toggle clicks
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-souls-wishlist-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        const productId = toggleBtn.dataset.productHandle || toggleBtn.dataset.productId;
        if (productId) {
          this.toggleProduct(productId);
        }
      }
    });

    // Observer to handle dynamically loaded products (AJAX)
    this.observer = new MutationObserver(() => this.syncButtons());
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  syncButtons() {
    const buttons = document.querySelectorAll('[data-souls-wishlist-toggle]');
    buttons.forEach(btn => {
      const productId = btn.dataset.productHandle || btn.dataset.productId;
      if (this.wishlist.includes(productId)) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || [];
    } catch (e) {
      return [];
    }
  }

  toggleProduct(productId) {
    let list = this.getWishlist();
    const index = list.indexOf(productId);
    
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(productId);
    }
    
    localStorage.setItem(this.storageKey, JSON.stringify(list));
    this.wishlist = list;
    
    document.dispatchEvent(new CustomEvent('souls:wishlist:update', {
      detail: { wishlist: list }
    }));
  }

  updateBadges() {
    const badges = document.querySelectorAll('[data-souls-wishlist-count]');
    const countText = document.querySelectorAll('[data-souls-wishlist-count-text]');
    const count = this.wishlist.length;
    
    badges.forEach(badge => {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });

    countText.forEach(el => {
      el.textContent = count === 1 ? 'You have 1 item in your wishlist' : `You have ${count} items in your wishlist`;
    });
  }

  async renderItems() {
    const activeContainer = this.pageGrid || this.container;
    if (!activeContainer) return;
    
    if (this.wishlist.length === 0) {
      activeContainer.innerHTML = `
        <div class="souls-wishlist-empty-state">
          <p>${activeContainer.dataset.emptyText || 'Your wishlist is currently empty.'}</p>
          <a href="/collections/all" class="button souls-button" onclick="if(window.closeSoulsWishlist) closeSoulsWishlist()">Explore Products</a>
        </div>
      `;
      return;
    }

    activeContainer.innerHTML = '<div class="souls-wishlist-loading"><div class="premium-loader"></div></div>';
    
    try {
      const products = await Promise.all(
        this.wishlist.map(async (identifier) => {
          // If it's a numeric ID, we can't easily fetch it via /products/id.js
          // so we skip it to prevent 404s, or we can improve this if we have a mapping.
          // For now, prioritize handles.
          if (!identifier || /^\d+$/.test(identifier)) return null;
          
          try {
            const response = await fetch(`/products/${identifier}.js`);
            if (!response.ok) return null;
            return await response.json();
          } catch (e) {
            return null;
          }
        })
      );

      const validProducts = products.filter(p => p !== null);

      if (validProducts.length === 0 && this.wishlist.length > 0) {
        // If we have items but none could be fetched, it might be due to ID vs Handle mismatch
        activeContainer.innerHTML = '<p class="text-center py-8">Wishlist items could not be loaded. Try adding them again.</p>';
        return;
      }

      const gridClass = this.pageGrid ? 'souls-wishlist-grid' : 'souls-wishlist-items-grid';
      const itemClass = this.pageGrid ? 'souls-wishlist-item' : 'souls-wishlist-drawer-item';

      activeContainer.innerHTML = `
        <div class="${gridClass}">
          ${validProducts.map(product => {
            const isDrawer = !this.pageGrid;
            return `
              <div class="${itemClass}" data-handle="${product.handle}">
                <a href="${product.url}" class="${isDrawer ? 'souls-wishlist-thumb-wrap' : 'souls-wishlist-item-image-link'}">
                  <img src="${product.featured_image || product.images[0] || ''}" alt="${product.title}" loading="lazy" class="${isDrawer ? 'souls-wishlist-thumb-img' : ''}">
                </a>
                <div class="souls-wishlist-item-details">
                  ${isDrawer ? '<div class="souls-wishlist-item-info">' : ''}
                    <a href="${product.url}" class="souls-wishlist-item-title">${product.title}</a>
                    <div class="souls-wishlist-item-price">${window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(product.price) : '$' + (product.price / 100).toFixed(2)}</div>
                  ${isDrawer ? '</div>' : ''}
                  <button class="souls-wishlist-remove" onclick="window.SoulsWishlistInstance.toggleProduct('${product.handle}')" aria-label="Remove item">
                    ${isDrawer ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>' : 'Remove Item'}
                  </button>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `;
    } catch (err) {
      activeContainer.innerHTML = '<p class="text-center py-8">An error occurred while loading your wishlist.</p>';
    }
  }
}

// Initialize on load
if (!window.SoulsWishlistInstance) {
  window.SoulsWishlistInstance = new SoulsWishlist();
}

// Global helper for opening/closing from anywhere
window.openSoulsWishlist = () => {
    document.documentElement.classList.add('souls-wishlist-active');
};
window.closeSoulsWishlist = () => {
    document.documentElement.classList.remove('souls-wishlist-active');
};

